import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Progress,
  Tabs,
  Spin,
  App,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  TeamOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import SearchCard from './SearchCard';
import CampaignCalendar from './CampaignCalendar';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import campaignService from '../services/campaignService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const CampaignList = () => {
  const navigate = useNavigate();
  const { message: messageApi } = App.useApp();

  const [campaigns, setCampaigns] = useState([]);
  const [campaignMeta, setCampaignMeta] = useState({ limit: 10, filtersApplied: false });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [dateRange, setDateRange] = useState([null, null]);

  const deriveProgress = (campaign) => {
    const overview = campaign.statistics?.overview;
    if (overview) {
      const total = overview.total || 0;
      const completed = overview.completed || 0;
      const pending = overview.pending || 0;
      const inProgress = overview.inProgress || 0;

      return {
        totalParticipants: total,
        completedParticipants: completed,
        pendingParticipants: pending,
        inProgressParticipants: inProgress,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
      };
    }

    return {
      totalParticipants: campaign.progress?.totalParticipants || 0,
      completedParticipants: campaign.progress?.completedParticipants || 0,
      pendingParticipants: campaign.progress?.pendingParticipants || 0,
      inProgressParticipants: campaign.progress?.inProgressParticipants || 0,
      completionRate: campaign.progress?.completionRate || 0
    };
  };

  const fetchCampaigns = async (params = {}) => {
    setLoading(true);
    try {
      const response = await campaignService.getAll(params);
      if (response.success) {
        const normalizedCampaigns = (response.data || []).map((campaign) => ({
          ...campaign,
          progress: deriveProgress(campaign)
        }));

        setCampaigns(normalizedCampaigns);
        setCampaignMeta(response.meta || { limit: 10, filtersApplied: false });
      } else {
        messageApi.error(response.message || 'Failed to fetch campaigns');
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      messageApi.error('Failed to fetch campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const buildQueryParams = (searchValue, statusValue, rangeValue) => {
    const params = {};

    if (searchValue && searchValue.trim()) {
      params.search = searchValue.trim();
    }

    if (statusValue && statusValue !== 'all') {
      params.status = statusValue;
    }

    if (Array.isArray(rangeValue) && rangeValue[0] && rangeValue[1]) {
      params.startDate = rangeValue[0].startOf('day').toISOString();
      params.endDate = rangeValue[1].endOf('day').toISOString();
    }

    return params;
  };

  const handleSearchSubmit = () => {
    const params = buildQueryParams(searchText, statusFilter, dateRange);
    fetchCampaigns(params);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    const params = buildQueryParams(searchText, value, dateRange);
    fetchCampaigns(params);
  };

  const handleOrganizationFilterChange = (value) => {
    setOrganizationFilter(value);
  };

  const handleDateRangeChange = (dates) => {
    const normalized = dates ? [dates[0], dates[1]] : [null, null];
    setDateRange(normalized);

    const hasCompleteRange = normalized[0] && normalized[1];
    const isCleared = !normalized[0] && !normalized[1];

    if (hasCompleteRange || isCleared) {
      const params = buildQueryParams(searchText, statusFilter, normalized);
      fetchCampaigns(params);
    }
  };

  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setOrganizationFilter('all');
    setDateRange([null, null]);
    fetchCampaigns();
  };

  const handleCreateNew = () => {
    navigate('/dashboard/campaigns/new');
  };

  const handleEdit = (campaign) => {
    if (campaign.status === 'active' || campaign.status === 'completed') {
      navigate(`/dashboard/campaigns/report/${campaign._id}`, {
        state: { campaign }
      });
    } else {
      navigate(`/dashboard/campaigns/edit/${campaign._id}`);
    }
  };

  const getProgressColor = (completionRate) => {
    if (completionRate >= 80) return '#52c41a';
    if (completionRate >= 50) return '#faad14';
    return '#ff4d4f';
  };

  const getStatusColors = (status) => {
    switch (status) {
      case 'active':
        return {
          borderColor: '#52c41a',
          backgroundColor: '#f6ffed',
          shadowColor: 'rgba(82, 196, 26, 0.15)'
        };
      case 'completed':
        return {
          borderColor: '#1890ff',
          backgroundColor: '#f0f5ff',
          shadowColor: 'rgba(24, 144, 255, 0.15)'
        };
      case 'paused':
        return {
          borderColor: '#faad14',
          backgroundColor: '#fffbe6',
          shadowColor: 'rgba(250, 173, 20, 0.15)'
        };
      case 'cancelled':
        return {
          borderColor: '#ff4d4f',
          backgroundColor: '#fff2f0',
          shadowColor: 'rgba(255, 77, 79, 0.15)'
        };
      default:
        return {
          borderColor: '#d9d9d9',
          backgroundColor: '#fafafa',
          shadowColor: 'rgba(0, 0, 0, 0.06)'
        };
    }
  };

  const getStatusTagColor = (status) => {
    switch (status) {
      case 'active':
        return '#52c41a';
      case 'completed':
        return '#1890ff';
      case 'paused':
        return '#fa8c16';
      case 'cancelled':
        return '#ff4d4f';
      default:
        return '#d9d9d9';
    }
  };

  const getUniqueOrganizations = () => {
    const organizations = new Set();
    campaigns.forEach((campaign) => {
      campaign.targets?.forEach((target) => {
        if (target.organizationName) {
          organizations.add(target.organizationName);
        }
      });
    });
    return Array.from(organizations).sort();
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesOrganization =
      organizationFilter === 'all' ||
      campaign.targets?.some((target) => target.organizationName === organizationFilter);
    return matchesOrganization;
  });

  const getOrganizationDetails = (campaign) => {
    if (!campaign) {
      return { count: 0, names: [] };
    }

    const summaryCount = campaign?.targetPaths?.summary?.uniqueBusinessUnits;
    const summaryNames = campaign?.targetPaths?.summary?.businessUnitNames;
    const namesSet = new Set();

    const addName = (name) => {
      if (typeof name === 'string') {
        const trimmed = name.trim();
        if (trimmed) {
          namesSet.add(trimmed);
        }
      }
    };

    if (Array.isArray(summaryNames)) {
      summaryNames.forEach(addName);
    }

    const displayPaths = campaign?.targetPaths?.displayPaths;
    if (Array.isArray(displayPaths) && displayPaths.length > 0) {
      displayPaths.forEach((path) => {
        if (typeof path === 'string') {
          const sanitizedPath = path.replace(/→/g, '/');
          const segments = sanitizedPath.split('/').map((segment) => segment.trim()).filter(Boolean);
          if (segments.length >= 2) {
            addName(segments[1]);
          }
        } else if (path?.businessUnitName) {
          addName(path.businessUnitName);
        }
      });
    }

    if (Array.isArray(campaign?.targets) && campaign.targets.length > 0) {
      campaign.targets.forEach((target) => {
        if (target?.organizationName) {
          addName(target.organizationName);
        } else if (target?.organizationId?.name) {
          addName(target.organizationId.name);
        } else if (target?.businessUnitName) {
          addName(target.businessUnitName);
        }
      });
    }

    const names = Array.from(namesSet);
    const count = names.length > 0
      ? names.length
      : (typeof summaryCount === 'number' && summaryCount > 0 ? summaryCount : 0);

    return { count, names };
  };

  const hasDateRange = dateRange?.[0] && dateRange?.[1];
  const isFilteredMode = Boolean(
    campaignMeta?.filtersApplied ||
    statusFilter !== 'all' ||
    organizationFilter !== 'all' ||
    hasDateRange
  );

  const renderCampaignCard = (campaign) => {
    const statusColors = getStatusColors(campaign.status);
    const organizationDetails = getOrganizationDetails(campaign);

    return (
      <Card
        key={campaign._id}
        style={{
          borderRadius: '8px',
          border: '1px solid #f0f0f0',
          borderTop: `4px solid ${statusColors.borderColor}`,
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        bodyStyle={{ padding: '16px', display: 'flex', flexDirection: 'column', height: '100%' }}
        onClick={() => handleEdit(campaign)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(46, 134, 171, 0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '7px'
          }}
        >
          <div style={{ flex: 1, marginRight: '8px', minWidth: 0 }}>
            <Title
              level={5}
              style={{
                margin: 0,
                marginBottom: '4px',
                color: '#2E86AB',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '1.2'
              }}
            >
              {(() => {
                const name = campaign.name;
                if (!name || typeof name !== 'string' || name.trim() === '') {
                  return 'Untitled Campaign';
                }

                let trimmedName = name.trim();
                const words = trimmedName.split(' ');
                if (words.length > 1) {
                  const halfLength = Math.floor(words.length / 2);
                  const firstHalf = words.slice(0, halfLength).join(' ');
                  const secondHalf = words.slice(halfLength, halfLength * 2).join(' ');

                  if (firstHalf && firstHalf === secondHalf) {
                    trimmedName = firstHalf;
                  }
                }

                const invalidNames = [
                  'At least one target must be selected',
                  'Please select at least one target',
                  'Please enter campaign name',
                  'Campaign name is required'
                ];

                if (invalidNames.includes(trimmedName)) {
                  return 'Untitled Campaign';
                }

                return trimmedName;
              })()}
            </Title>
          </div>
          <div
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: getStatusTagColor(campaign.status),
              flexShrink: 0,
              marginTop: '2px',
              border: '2px solid white',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
            }}
            title={`Status: ${(campaign.status || 'draft').replace(/^(.)/, (match) => match.toUpperCase())}`}
          />
        </div>

        <div
          style={{
            marginBottom: '4px',
            minHeight: '40px',
            maxHeight: '40px',
            overflow: 'hidden'
          }}
        >
          <Text
            style={{
              fontSize: '13px',
              color: '#666',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: '1.4',
              maxHeight: '40px'
            }}
          >
            {(() => {
              const description = campaign.description || 'No description provided';
              if (typeof description === 'string' && description !== 'No description provided') {
                const words = description.split(' ');
                if (words.length > 2) {
                  const halfLength = Math.floor(words.length / 2);
                  const firstHalf = words.slice(0, halfLength).join(' ');
                  const secondHalf = words.slice(halfLength, halfLength * 2).join(' ');
                  if (firstHalf && firstHalf === secondHalf) {
                    return firstHalf;
                  }
                }
                return description.length > 100 ? `${description.substring(0, 100)}...` : description;
              }
              return description;
            })()}
          </Text>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <Text
            strong
            style={{
              fontSize: '13px',
              color: '#2E86AB',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {(() => {
              const title = campaign.questionnaire?.title || 'Quiz';
              if (typeof title === 'string') {
                const words = title.split(' ');
                if (words.length > 1) {
                  const halfLength = Math.floor(words.length / 2);
                  const firstHalf = words.slice(0, halfLength).join(' ');
                  const secondHalf = words.slice(halfLength, halfLength * 2).join(' ');
                  if (firstHalf && firstHalf === secondHalf) {
                    return firstHalf;
                  }
                }
                return title.length > 40 ? `${title.substring(0, 40)}...` : title;
              }
              return 'Quiz';
            })()}
          </Text>
        </div>

        <div style={{ marginBottom: '5px', flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <Text style={{ fontSize: '12px', color: '#666' }}>Progress</Text>
            <Text style={{ fontSize: '12px', fontWeight: 'bold' }}>
              {campaign.progress?.completionRate || 0}%
            </Text>
          </div>
          <Progress
            percent={campaign.progress?.completionRate || 0}
            showInfo={false}
            strokeColor={getProgressColor(campaign.progress?.completionRate || 0)}
            style={{ marginBottom: '3px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2E86AB' }}>
                  {campaign.progress?.totalParticipants || 0}
                </div>
                <div style={{ fontSize: '9px', color: '#666' }}>Total</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#52c41a' }}>
                  {campaign.progress?.completedParticipants || 0}
                </div>
                <div style={{ fontSize: '9px', color: '#666' }}>Done</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>
                  {campaign.progress?.inProgressParticipants || 0}
                </div>
                <div style={{ fontSize: '9px', color: '#666' }}>Active</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#faad14' }}>
                  {campaign.progress?.pendingParticipants || 0}
                </div>
                <div style={{ fontSize: '9px', color: '#666' }}>Pending</div>
              </div>
            </Space>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '5px',
            borderTop: '1px solid #f0f0f0',
            marginTop: 'auto'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CalendarOutlined style={{ color: '#666', fontSize: '12px' }} />
            <Text style={{ fontSize: '11px', color: '#666' }}>
              {campaignService.formatDateRange(campaign.startDate, campaign.endDate)}
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <TeamOutlined style={{ color: '#666', fontSize: '12px' }} />
            <Tooltip
              title={(
                <div style={{ maxWidth: 240 }}>
                  <Text strong style={{ fontSize: '12px', marginBottom: '4px' }}>
                    Organizations
                  </Text>
                  <div
                    style={{
                      maxHeight: 180,
                      overflowY: organizationDetails.names.length > 6 ? 'auto' : 'visible'
                    }}
                  >
                    {organizationDetails.names.length > 0 ? (
                      organizationDetails.names.map((name) => (
                        <div key={`${campaign._id}-${name}`} style={{ fontSize: '11px', color: '#555' }}>
                          {name}
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: '11px', color: '#888' }}>No organizations selected yet</div>
                    )}
                  </div>
                </div>
              )}
              placement="topRight"
              mouseEnterDelay={0.3}
            >
              <Text style={{ fontSize: '11px', color: '#666' }}>
                {`${organizationDetails.count} organization${organizationDetails.count === 1 ? '' : 's'}`}
              </Text>
            </Tooltip>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        title="My Campaigns"
        breadcrumb={BREADCRUMB_PATTERNS.campaigns}
        rightContent={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew}>
            Add New Campaign
          </Button>
        }
      />

      <SearchCard
        searchPlaceholder="Search for campaigns"
        searchValue={searchText}
        onSearchChange={(e) => setSearchText(e.target.value)}
        onSearchClick={handleSearchSubmit}
        theme="white"
        filters={[
          {
            label: 'Status',
            value: statusFilter,
            onChange: handleStatusFilterChange,
            width: 150,
            options: [
              { value: 'all', label: 'View all' },
              { value: 'draft', label: 'Draft' },
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' }
            ]
          },
          {
            label: 'Organisation',
            value: organizationFilter,
            onChange: handleOrganizationFilterChange,
            width: 200,
            options: [
              { value: 'all', label: 'View all' },
              ...getUniqueOrganizations().map((org) => ({
                value: org,
                label: org
              }))
            ]
          },
          {
            label: 'Date range',
            type: 'dateRange',
            value: dateRange,
            onChange: handleDateRangeChange,
            width: 260
          }
        ]}
        actionButtons={[
          {
            type: 'primary',
            text: 'Search',
            icon: <SearchOutlined />, 
            onClick: handleSearchSubmit
          },
          {
            text: 'Reset',
            icon: <ReloadOutlined />, 
            onClick: handleResetFilters
          }
        ]}
      />

      <div style={{ marginBottom: '16px' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Grid view" key="all" />
          <TabPane tab="Calendar view" key="calendar" />
        </Tabs>
      </div>

      {activeTab === 'calendar' ? (
        <CampaignCalendar campaigns={filteredCampaigns} />
      ) : (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text>Loading campaigns...</Text>
              </div>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ color: '#999', fontSize: '48px', marginBottom: '16px' }}>
                <TeamOutlined />
              </div>
              <Title level={4} style={{ color: '#999' }}>
                {isFilteredMode ? 'No campaigns match your filters' : 'No campaigns available'}
              </Title>
              <Text type="secondary">
                {isFilteredMode
                  ? 'Try adjusting your search, status, or date filters to broaden the results.'
                  : 'Create your first campaign using the button above'}
              </Text>
            </Card>
          ) : (
            <Row gutter={[16, 16]}>
              {filteredCampaigns.map((campaign) => (
                <Col xs={24} sm={12} md={8} lg={6} key={campaign._id}>
                  {renderCampaignCard(campaign)}
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignList;
